import { Box, Skeleton, Stack, TooltipProps, Typography } from '@mui/material'
import { TypographyVariant } from '@mui/material/styles/createTypography'
import { C1Chip, C1ChipProps } from 'c1ui/components/C1Chip'
import { C1Tooltip } from 'c1ui/components/C1Tooltip'
import { getAvatarSizeStyles } from 'components/core/avatar/AvatarSizes'
import { C1StyledLink } from 'components/core/links/C1Link'
import { C1TextWithTruncation } from 'components/core/TextWithTruncation/C1TextWithTruncation'
import { DrawerVariants } from 'components/no-ui/context/drawer-provider'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { useDrawer } from 'hooks/useDrawer'
import { Route } from 'lib/routes'
import React, { ReactNode, useCallback } from 'react'
import { DisplayNameDetailsStyles } from './styles'

type C1DisplayNameSkeletonProps = {
    size?: 'sm' | 'md' | 'lg'
}

export const C1DisplayNameSkeleton: React.FC<C1DisplayNameSkeletonProps> = ({ size = 'md' }) => (
    <Stack direction="row" alignItems="center" spacing={1}>
        <Skeleton variant="circular" sx={{ ...getAvatarSizeStyles(size) }} />
        <Skeleton sx={{ height: 1, width: '80px' }} />
    </Stack>
)

/**
 * @designSystem
 */
export type DisplayNameDetailsProps = {
    avatar?: ReactNode
    /** Primary name or title text. */
    displayName: string
    drawerContent?: React.ReactElement
    drawerVariant?: DrawerVariants
    link?: Route
    details?: ReactNode
    readOnly?: boolean
    tooltipContent?: ReactNode
    showTooltip?: boolean
    displayNameVariant?: TypographyVariant
    padded?: boolean
    isDeleted?: boolean
    identifier?: C1ChipProps
    status?: React.ReactElement
    margin?: boolean
    fullWidth?: boolean,
    maxWidth?: string,
    displayNameMetadata?: React.ReactElement
    tooltipProps?: Partial<TooltipProps>
    actionName?: string
}

/**
 * Avatar + name + details pattern used throughout C1 for displaying entities.
 *
 * @designSystem
 * @category core
 * @relatedComponents C1Chip, C1UsersList
 */
export const DisplayNameDetails = ({
    avatar,
    displayName,
    drawerContent,
    drawerVariant = 'sm',
    link,
    details,
    readOnly = false,
    tooltipContent,
    showTooltip = false,
    displayNameVariant = 'body1',
    padded = false,
    isDeleted = false,
    identifier,
    status,
    margin = false,
    fullWidth = displayNameVariant?.charAt(0) === 'h',
    maxWidth = 'auto',
    displayNameMetadata,
    tooltipProps,
    actionName,
}: DisplayNameDetailsProps): React.ReactElement => {
    const [showDrawer] = useDrawer()

    const onDrawerClick = useCallback((): void => {
        if (drawerContent) {
            showDrawer(drawerContent, { variant: drawerVariant })
        }
    }, [showDrawer, drawerContent, drawerVariant])
    let avatarSx = margin ? DisplayNameDetailsStyles.item : DisplayNameDetailsStyles.itemLessMargin

    if (padded) {
        avatarSx = sxc(DisplayNameDetailsStyles.item, DisplayNameDetailsStyles.padded)
    }

    let textElement: ReactNode

    if (displayName !== '') {
        /* Don't truncate on titles. Today our titles are H*'s. */
        textElement = fullWidth
            ? <Typography variant={displayNameVariant} noWrap title={displayName} sx={{ color: 'inherit' }}>
                {displayName}
            </Typography>
            : <C1TextWithTruncation
                text={displayName}
                variant={displayNameVariant}
                textStyles={{ maxWidth, overflow: 'hidden', color: 'inherit' }}
                /* If this component is showing it's custom tooltip, don't show the
                tooltip within the truncation component */
                showTooltip={!showTooltip}
            />
    } else {
        textElement = false
    }

    const displayNameElement =
        <Box display="inline-flex" alignItems="center" sx={{ verticalAlign: 'middle', maxWidth }}>
            {avatar && <Box sx={avatarSx}>{avatar}</Box>}
            <Box sx={{ maxWidth, overflow: 'hidden' }}>
                <Stack gap={1} direction="row" alignItems="center">
                    <Stack direction="row" gap={1} alignItems="center" sx={{ maxWidth, overflow: 'hidden' }}>
                        {textElement}
                        {displayNameMetadata}
                    </Stack>
                    {identifier && <Box><C1Chip label={identifier.label} color={identifier.color} /></Box>}
                    {isDeleted && <Box><C1Chip color="error" label="Deleted" /></Box>}
                    {status && <Box>{status}</Box>}
                </Stack>
                {details && (
                    <Box>
                        <Typography variant="caption" color="grey.600" sx={DisplayNameDetailsStyles.wordBreak}>
                            {details}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>

    const onDrawerKeyDown = useCallback((e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onDrawerClick()
        }
    }, [onDrawerClick])

    // If we have a link or drawer content, wrap the entire element in a clickable component
    let linkedElement = displayNameElement
    if (!readOnly) {
        if (drawerContent) {
            linkedElement = <Box
                onClick={onDrawerClick}
                onKeyDown={onDrawerKeyDown}
                role="button"
                tabIndex={0}
                data-dd-action-name={actionName}
                sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover, &:focus-visible': {
                        textDecoration: 'underline',
                    },
                }}
            >
                {displayNameElement}
            </Box>
        } else if (link) {
            linkedElement = <C1StyledLink href={link} data-dd-action-name={actionName} sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                {displayNameElement}
            </C1StyledLink>
        }
    }

    // Add hover affordance if the element is interactive
    if ((!readOnly && (link || drawerContent)) || (showTooltip && tooltipContent)) {
        linkedElement = <Box>
            {linkedElement}
        </Box>
    }

    if (showTooltip && tooltipContent) {
        const styledTooltipContent = <Box p={1}>{tooltipContent}</Box>

        return <C1Tooltip
            title={styledTooltipContent}
            placement="right-end"
            {...tooltipProps}
        >
            {linkedElement}
        </C1Tooltip>
    }

    return linkedElement
}
